<template>
  <div
    :class="[
      'flex w-full items-center gap-3 px-4 py-3 text-left cursor-pointer',
      selected ? 'bg-primary-50' : 'bg-white'
    ]"
    :data-cy="`technician-row-${resource.id}`"
    role="button"
    :aria-pressed="selected"
    @click="emit('toggle', resource.id)"
  >
    <!-- Stopped here so a tap on the box toggles once rather than twice: the
         row itself is also a toggle target, per the story's tinted-row behaviour. -->
    <span class="shrink-0" @click.stop>
      <Checkbox
        :model-value="selected"
        @update:model-value="emit('toggle', resource.id)"
      />
    </span>

    <div
      v-if="isUnassigned"
      class="h-10 w-10 shrink-0 rounded-full border-2 border-dashed border-gray-400"
      data-cy="unassigned-avatar"
    ></div>
    <Avatar
      v-else
      class="shrink-0"
      :alt="resource.full_name"
      :src="resource.avatar?.url"
      :initials="getInitials(resource)"
      :status="resource.is_clocked_in ? 'online' : 'offline'"
    />

    <div class="flex min-w-0 flex-col">
      <span class="truncate font-medium text-gray-900">
        {{ resource.full_name }}
      </span>
      <span class="truncate text-xs font-normal text-gray-500">
        {{ subtitle }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

import Checkbox from "@/components/Seranova/Checkbox/Checkbox.vue";
import Avatar from "@/components/Seranova/Indicator/Avatar.vue";
import { capitalize, getInitials } from "@/mixins";
import { type SelectableResource } from "@/SchedulerV2/useTechnicianSelection";
import { UNASSIGNED_RESOURCE_ID } from "@/typings/scheduler";

/**
 * One selectable row in the technician sheet.
 *
 * Deliberately not built on ResourceContent, which renders the technician's
 * name as a link (and nests a second anchor behind it): navigating away is the
 * wrong response to a tap in a sheet whose rows toggle a selection. Fed the
 * Unassigned pseudo-row it would also render a red "offline" dot and a literal
 * "--" skill level, neither of which the story asks for.
 *
 * The Unassigned variant is derived from the row's own id rather than passed as
 * a flag, so the variant and the data cannot disagree.
 */
const props = withDefaults(
  defineProps<{
    resource: SelectableResource;
    selected?: boolean;
    unassignedCount?: number;
  }>(),
  { selected: false, unassignedCount: 0 }
);

const emit = defineEmits<{
  (e: "toggle", id: number): void;
}>();

const isUnassigned = computed(
  () => props.resource.id === UNASSIGNED_RESOURCE_ID
);

const subtitle = computed(() => {
  if (isUnassigned.value) {
    const noun =
      props.unassignedCount === 1 ? "appointment needs" : "appointments need";
    return `${props.unassignedCount} ${noun} a technician`;
  }

  const level = props.resource.installer
    ? "Installer"
    : capitalize(props.resource.skill_level || "--");

  return [level, props.resource.department?.name].filter(Boolean).join(" · ");
});
</script>
